/**
 * Котослоты: три барабана, одна линия, честный выбор по хешу.
 *
 * Каждый барабан крутится независимо: из одного хеша берутся три непересекающихся
 * среза, поэтому результат воспроизводится на любой платформе. Выплата считается
 * по одной линии: платит только тройка одинаковых символов.
 *
 * Отдача задаётся таблицей выплат и весами, а не отдельной ручкой. Считать её
 * нужно полным перебором комбинаций: слот маленький (шесть символов), и так
 * мы получаем точное число, а не оценку по симуляции.
 */
import { computePayout } from "../money.js";
import { weightedPick } from "../fairness.js";
/**
 * Лента барабана. Веса в десятых долях процента: сумма 1000.
 *
 * Тройка платит x1.2 / x1.5 / x1.8 / x2 / x2.4 / x3. Потолок в 3x переносит всю
 * отдачу на частоту попаданий: при редких тройках такая таблица давала бы 5%
 * отдачи, поэтому лента намеренно перекошена в сторону «Лапки». Итог — тройка
 * примерно в 41.6% спинов, отдача 50.02%, и почти все выигрыши мелкие.
 *
 * Низкий потолок множителя даёт заведению ещё и предсказуемость: максимальная
 * выплата равна трём ставкам, поэтому банк не проседает от одного спина.
 */
export const SLOT_SYMBOLS = [
    { id: "paw", name: "Лапка", emoji: "🐾", weight: 746, payout3: 120, payout2: 0 },
    { id: "fish", name: "Рыбка", emoji: "🐟", weight: 90, payout3: 150, payout2: 0 },
    { id: "yarn", name: "Клубок", emoji: "🧶", weight: 70, payout3: 180, payout2: 0 },
    { id: "milk", name: "Молоко", emoji: "🥛", weight: 50, payout3: 200, payout2: 0 },
    { id: "bell", name: "Бубенчик", emoji: "🔔", weight: 30, payout3: 240, payout2: 0 },
    { id: "crown", name: "Корона", emoji: "👑", weight: 14, payout3: 300, payout2: 0 },
];
/** Максимальный множитель таблицы: по нему считается риск казны на одну ставку. */
export function slotsMaxMultiplier(symbols = SLOT_SYMBOLS) {
    return symbols.reduce((max, symbol) => Math.max(max, symbol.payout3), 0);
}
/** Смещение среза хеша на барабан: 13 hex-символов = 52 бита, без пересечений. */
const REEL_HASH_OFFSETS = [0, 13, 26];
export function spinSlots(hashHex, symbols = SLOT_SYMBOLS) {
    const picked = REEL_HASH_OFFSETS.map((offset) => weightedPick(symbols, hashHex, offset));
    const reels = [picked[0].item, picked[1].item, picked[2].item];
    const { multiplier, kind } = linePayout(reels);
    return {
        reels,
        indexes: [picked[0].index, picked[1].index, picked[2].index],
        rolls: [picked[0].roll, picked[1].roll, picked[2].roll],
        multiplier,
        kind,
    };
}
export function linePayout(reels) {
    const [left, middle, right] = reels;
    if (!left || !middle || !right) {
        throw new Error("На линии должно быть три символа");
    }
    if (left.id === middle.id && middle.id === right.id) {
        return { multiplier: left.payout3, kind: "three" };
    }
    return { multiplier: 0, kind: "none" };
}
export function slotsPayout(stake, multiplier) {
    return computePayout(stake, multiplier);
}
/**
 * Точная отдача слота в базисных пунктах.
 *
 * Три независимых барабана с одинаковой лентой: вероятность комбинации равна
 * произведению весов, делённому на куб суммы. Перебор полный, поэтому тесту
 * не нужно гадать по выборке.
 */
export function slotsRtpBp(symbols = SLOT_SYMBOLS) {
    const total = symbols.reduce((sum, symbol) => sum + symbol.weight, 0);
    if (total <= 0)
        throw new Error("Сумма весов барабана должна быть положительной");
    let weighted = 0;
    const cube = total * total * total;
    for (const left of symbols) {
        for (const middle of symbols) {
            for (const right of symbols) {
                const { multiplier } = linePayout([left, middle, right]);
                weighted += left.weight * middle.weight * right.weight * multiplier;
            }
        }
    }
    // multiplier в сотых: среднее 96 означает 0.96x, то есть 9600 базисных пунктов
    return Math.round((weighted * 100) / cube);
}
export function assertSlotsAreSane(symbols = SLOT_SYMBOLS, maxRtpBp = 9900) {
    const rtp = slotsRtpBp(symbols);
    if (rtp > maxRtpBp) {
        throw new Error(`Слот отдаёт ${(rtp / 100).toFixed(2)}% при допустимых ${(maxRtpBp / 100).toFixed(2)}%`);
    }
    if (!symbols.some((symbol) => symbol.payout3 > 100)) {
        throw new Error("В слоте нет выигрышной тройки выше ставки");
    }
}
//# sourceMappingURL=slots.js.map